import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import ChooseDate from 'shell/components/shared/choice-date';
import ListGender from 'shell/components/shared/gender';
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { noop } from 'shell/utils/noop';
import { CertificationFormVM } from 'front-office/view-models/document-birth-form';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { DatePicker } from '@mui/lab';
import { ResponseAnprData } from 'shell/models/person';
import { AnprVM } from 'shell/view-models/search-anpr';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { transformerPersonGeneralityList } from './transformer-anpr';
import { HttpResponse } from 'shell/models/http-response';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { transformSearchPersonDataset } from 'shell/utils/transform-search-person';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ItemVM } from 'shell/view-models/item';
import { PersonVM } from 'shell/view-models/search-person';
import MunicipalityForeignList from 'shell/components/shared/municipality-foreign-list';
import Table from 'shell/components/shared/table';
import { HeadCell } from 'shell/components/shared/table/types';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { PmiButton } from 'shell/components/shared/button';
import { useUserProfile } from 'shell/hooks/use-user-profile';

const headCells: HeadCell<PersonVM, keyof PersonVM>[] = [
  {
    id: 'fiscalCode',
    label: 'Codice Fiscale',
  },
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'gender',
    label: 'Sesso',
  },
  {
    id: 'birthday',
    label: 'Data di nascita',
  },
  {
    id: 'birthplace',
    label: 'Luogo di nascita',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'state',
    label: 'Indirizzo/Stato',
  },
  {
    id: 'anpr',
    label: 'ANPR',
  },
  {
    id: 'action',
    label: 'Azione',
  },
];

const url = `${__PERSON_NEW_API_URL__}/persona/ricerca-certificati`;

const CertificationComponent = (): React.ReactElement => {
  const formContext = useFormikContext<CertificationFormVM>();

  const { userProfile } = useUserProfile();

  const [visibilityTable, setVisibilityTable] = useState(false);

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [paginationState, setPaginationState] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  // Reset state pagination
  const resetPagination = useEventCallback(() => {
    setPaginationState({
      page: 0,
      rowsPerPage: 10,
    });
  });

  // Handle change page
  const handleChangePage = useEventCallback((page: number) => {
    setPaginationState((oldState) => ({ ...oldState, page }));
  });

  // Handle change rows per page
  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setPaginationState((oldState) => ({ ...oldState, page: 0, rowsPerPage }));
  });

  const [anpr, getAnpr] = useHttpFetch<HttpResponse<ResponseAnprData>, AnprVM<CertificationFormVM>>(
    url,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonGeneralityList,
    }
  );

  const getPerson = useHttpClient<HttpResponse<ResponseAnprData>, AnprVM<CertificationFormVM>>(
    url,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonGeneralityList,
    }
  );

  const showTooltip = useMemo(() => {
    if (
      formContext.values?.fiscalCode ||
      (formContext.values?.name && formContext.values?.surname)
    ) {
      if (formContext.errors?.searchApplicant) {
        return true;
      }
      return false;
    }

    if (
      !formContext.values?.fiscalCode ||
      (!formContext.values?.name && !formContext.values?.surname)
    ) {
      return true;
    }

    return false;
  }, [formContext.values, formContext.errors.searchApplicant]);

  const tooltipMessage = useMemo(() => {
    if (
      formContext.values?.fiscalCode ||
      (formContext.values?.name && formContext.values?.surname)
    ) {
      if (formContext.errors?.searchApplicant) {
        return formContext.errors?.searchApplicant;
      }
      return;
    }
    if (
      !formContext.values?.fiscalCode ||
      (!formContext.values?.name && !formContext.values?.surname)
    ) {
      return 'Inserire codice fiscale o cognome e nome';
    }
  }, [formContext.values, formContext.errors?.searchApplicant]);

  const handleSearch = useEventCallback(() => {
    setVisibilityTable(false);

    if (
      !formContext.values?.fiscalCode &&
      !(formContext.values?.name && formContext.values?.surname)
    ) {
      return;
    }

    const data = transformSearchPersonDataset({
      page: 0,
      rowsPerPage: 0,
      orders: {},
      subjectData: {
        fiscalCode: formContext.values?.fiscalCode,
        name: formContext.values?.name,
        surname: formContext.values?.surname,
        withoutName: formContext.values?.withoutName,
        withoutSurname: formContext.values?.withoutSurname,
        gender: formContext.values?.gender,
        birthday: formContext.values?.birthDate,
        birthdayChoice: formContext.values?.birthChoiceDate,
        isForeign: formContext.values?.isForeign,
        foreignMunicipality: formContext.values?.foreignMunicipality,
        foreignStateBirth: formContext.values?.foreignState,
        municipalityOfBirth: formContext.values?.municipality,
        provinceOfBirth: formContext.values?.municipality?.province || undefined,
        // se feature auth anpr === 2
        isNationalResearch: userProfile?.datiLogin?.profile?.feature?.anpr === '2' ? true : false,
        municipalityOfResidence:
          userProfile?.datiLogin?.profile?.feature?.anpr === '1'
            ? {
                id: userProfile?.datiLogin?.codiceIstat || '',
                description: userProfile?.datiLogin?.nomeComune || '',
                province: userProfile?.datiLogin?.siglaProvincia || '',
              }
            : undefined,
      },
      identityCard: {
        number: formContext.values?.documentNumber,
        // TODO data rilascio
      },
    });

    (
      getAnpr({
        data,
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<CertificationFormVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<CertificationFormVM>;

      if (castedResponse?.errors?.length > 0) {
        window.scrollTo(0, 0);
        setVisibility({});
        return;
      }

      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];
        formContext.setValues({
          ...formContext.values,
          ...element,
          searchApplicant: true,
        });
        window.scrollTo(0, 0);
        return;
      }
      setVisibilityTable(true);
      resetPagination();
    });
  });

  const handleClickSelect = useEventCallback((person: CertificationFormVM) => {
    (
      getPerson({
        data: {
          criteriRicerca: {
            codiceFiscale: person.fiscalCode || undefined,
            idSchedaSoggettoANPR: person.idSubjectAnpr,
          },
          datiRichiesta: {
            comuneResidenza:
              userProfile?.datiLogin?.profile?.feature?.anpr === '1'
                ? {
                    codiceIstat: userProfile?.datiLogin?.codiceIstat,
                    nomeComune: userProfile?.datiLogin?.nomeComune,
                    siglaProvinciaIstat: userProfile?.datiLogin?.siglaProvincia,
                  }
                : undefined,
            ricercaNazionale: userProfile?.datiLogin?.profile?.feature?.anpr === '2' ? '1' : '0',
          },
        },
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<CertificationFormVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<CertificationFormVM>;
      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];
        formContext.setValues({
          ...formContext.values,
          ...element,
          searchApplicant: true,
        });
        window.scrollTo(0, 0);
        setVisibilityTable(false);
      }
      resetPagination();
    });
  });

  const castedAnprData = anpr.data as AnprVM<CertificationFormVM>;

  const dataPersons: Array<PersonVM> = useMemo(() => {
    return (
      castedAnprData?.persons?.map((person) => ({
        fiscalCode: person.fiscalCode || '',
        surname: person.surname || '',
        name: person.name || '',
        gender: person.gender || '',
        birthday: person.birthDate?.toFormat('dd/MM/yyyy') || '',
        birthplace: person.birthplace,
        residence: person.residenceString || '',
        state: person.state,
        anpr: person.idSubjectAnpr,
        action: (
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => handleClickSelect(person)}
          >
            Seleziona
          </PmiButton>
        ),
      })) || []
    );
  }, [castedAnprData?.persons, handleClickSelect]);

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, anpr.data]);

  const onChangeGender = useEventCallback((event: SelectChangeEvent<string>) => {
    formContext.setFieldValue('gender', event.target.value);
  });

  const onChangeChooseDate = useEventCallback(
    (date: DateTime | null, choice?: Array<DatePickerView>) => {
      formContext.setValues({
        ...(formContext.values || {}),
        birthDate: date,
        birthChoiceDate: choice,
      });
    }
  );

  const onChangeMunicipality = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    formContext.setFieldValue('municipality', municipality);
  });

  const onChangeForeignMunicipality = useEventCallback(
    (foreignMunicipality: MunicipalityAnprVM | null) => {
      formContext.setFieldValue('foreignMunicipality', foreignMunicipality);
    }
  );

  const onChangeMunicipalityDocument = useEventCallback(
    (municipalityDocument: MunicipalityAnprVM | null) => {
      formContext.setFieldValue('municipalityDocument', municipalityDocument);
    }
  );

  const onChangeRadio = useEventCallback(() => {
    formContext.setValues({
      ...(formContext.values || {}),
      municipality: null,
      foreignState: null,
      foreignMunicipality: null,
      isForeign: !formContext.values.isForeign,
    });
  });

  const onChangeRadioDocument = useEventCallback(() => {
    formContext.setValues({
      ...(formContext.values || {}),
      municipalityDocument: null,
      foreignStateDocument: '',
      isForeignDocument: !formContext.values.isForeignDocument,
    });
  });

  const onChangeForeignState = useEventCallback(
    (foreignState: ForeignStateVM | null | undefined) => {
      formContext.setFieldValue('foreignState', foreignState);
    }
  );

  return (
    <Fragment>
      {visibilityTable || errorMessage.visibility ? (
        <div className="pmi-pt-2">
          <SearchMessages
            type={MessageType.Error}
            title={errorMessage.title || ''}
            messages={errorMessage.messages || []}
            visibility={errorMessage.visibility || false}
            onChangeVisibility={onVisibility}
          />
          <SearchMessages
            type={MessageType.Warning}
            title={`Ci sono ${castedAnprData?.warnings?.length || ''} warning`}
            messages={castedAnprData?.warnings || []}
            visibility={
              visibility.warning !== undefined
                ? visibility.warning
                : (castedAnprData?.warnings || [])?.length > 0
            }
            onChangeVisibility={onVisibility}
          />
        </div>
      ) : undefined}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Codice fiscale *"
            aria-label="Codice fiscale"
            name="fiscalCode"
            id="fiscalCode"
            fullWidth
            value={formContext.values?.fiscalCode?.toUpperCase() || ''}
            onChange={(event) => formContext.setFieldValue('fiscalCode', event.target.value)}
            inputProps={{
              maxLength: '16',
            }}
            error={formContext.touched?.fiscalCode && Boolean(formContext.errors?.fiscalCode)}
            helperText={formContext.errors?.fiscalCode}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Cognome *"
            aria-label="Cognome"
            id="surname"
            fullWidth
            value={formContext.values?.surname || ''}
            onChange={(event) => formContext.setFieldValue('surname', event.target.value)}
            error={formContext.touched?.surname && Boolean(formContext.errors?.surname)}
            helperText={formContext.errors?.surname}
            disabled={formContext.values.withoutSurname}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutSurname"
                checked={formContext.values.withoutSurname || false}
                onChange={() => {
                  formContext.setFieldValue('withoutSurname', !formContext.values.withoutSurname);
                  formContext.setFieldValue('surname', '');
                }}
                inputProps={{ 'aria-label': 'senza cognome' }}
              />
            }
            label="Persona senza cognome"
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Nome *"
            aria-label="Nome"
            id="name"
            fullWidth
            value={formContext.values.name || ''}
            onChange={(event) => formContext.setFieldValue('name', event.target.value)}
            error={formContext.touched?.name && Boolean(formContext.errors?.name)}
            helperText={formContext.errors?.name}
            disabled={formContext.values.withoutName}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutName"
                checked={formContext.values.withoutName || false}
                onChange={() => {
                  formContext.setFieldValue('withoutName', !formContext.values.withoutName);
                  formContext.setFieldValue('name', '');
                }}
                inputProps={{ 'aria-label': 'senza nome' }}
              />
            }
            label="Persona senza nome"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ListGender
            value={formContext.values.gender || ''}
            onChange={onChangeGender}
            required
            error={formContext.touched?.gender && Boolean(formContext.errors?.gender)}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12} container spacing={2} alignItems="center">
            <ChooseDate
              label="Data di nascita"
              selected={formContext.values.birthDate || null}
              onChange={onChangeChooseDate}
              required
              choice={formContext.values.birthChoiceDate}
              helperText={formContext.errors?.birthDate}
              error={formContext.touched?.birthDate && Boolean(formContext.errors?.birthDate)}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Nascita"
              id="isForeign"
              defaultValue={false}
              value={formContext.values.isForeign || false}
              onChange={() => onChangeRadio()}
            >
              <FormControlLabel value="false" control={<Radio />} label="Nascita Italia" />
              <FormControlLabel value="true" control={<Radio />} label="Nascita Estero" />
            </RadioGroup>
          </FormControl>
        </Grid>
        {formContext.values.isForeign ? (
          <Fragment>
            <Grid item xs={12} md={4}>
              <ForeignStateList
                value={formContext.values.foreignState || null}
                onChange={onChangeForeignState}
                label="Stato estero di nascita"
                id="foreignStateBirth"
                required
                error={
                  formContext.touched?.foreignState && Boolean(formContext.errors?.foreignState)
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MunicipalityForeignList
                value={formContext.values.foreignMunicipality || null}
                onChange={onChangeForeignMunicipality}
                label="Comune estero *"
                id="foreignMunicipality"
                disabled={!formContext.values.foreignState}
                foreignStateIstatCode={formContext.values.foreignState?.id}
                error={
                  formContext.touched?.foreignMunicipality &&
                  Boolean(formContext.errors?.foreignMunicipality)
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                aria-label="Provincia o Contea"
                label="Provincia/Contea"
                id="foreignProvince"
                name="foreignProvince"
                fullWidth
                type="text"
                disabled
                variant="outlined"
                value={formContext.values.foreignMunicipality?.province || ''}
              />
            </Grid>
          </Fragment>
        ) : (
          <Fragment>
            <Grid item xs={12} md={4}>
              <MunicipalityList
                label="Comune"
                fromDate={formContext.values.birthDate}
                required
                value={formContext.values.municipality || null}
                onChange={onChangeMunicipality}
                error={
                  formContext.touched?.municipality && Boolean(formContext.errors?.municipality)
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                label="Provincia"
                aria-label="Provincia"
                id="province"
                name="province"
                fullWidth
                onChange={noop}
                value={formContext.values.municipality?.province || ''}
                disabled
              />
            </Grid>
          </Fragment>
        )}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <PmiTitleDivider>Documento di riconoscimento</PmiTitleDivider>
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              id="isForeignDocument"
              defaultValue={false}
              value={formContext.values.isForeignDocument || false}
              onChange={() => onChangeRadioDocument()}
            >
              <FormControlLabel value="false" control={<Radio />} label="Carta d'identità" />
              <FormControlLabel value="true" control={<Radio />} label="Altro" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Numero *"
            aria-label="Numero"
            fullWidth
            id="documentNumber"
            inputProps={{
              maxLength: '50',
            }}
            onChange={(event) => formContext.setFieldValue('documentNumber', event.target.value)}
            value={formContext.values.documentNumber || ''}
            helperText={formContext.errors?.documentNumber}
            error={
              formContext.touched?.documentNumber && Boolean(formContext.errors?.documentNumber)
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Data rilascio *"
            aria-label="Data rilascio"
            value={formContext.values.documentIssuedDate || null}
            onChange={(value) => formContext.setFieldValue('documentIssuedDate', value)}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="documentIssuedDate"
                fullWidth
                error={
                  formContext.touched?.documentIssuedDate &&
                  Boolean(formContext.errors?.documentIssuedDate)
                }
                helperText={formContext.errors?.documentIssuedDate}
              />
            )}
          />
        </Grid>
        <Grid item container spacing={2} alignItems="center">
          {formContext.values.isForeignDocument ? (
            <>
              <Grid item xs={12} md={7}>
                <PmiTextField
                  label="Ente/Luogo Rilascio *"
                  aria-label="Ente Luogo Rilascio"
                  id="release-location"
                  fullWidth
                  onChange={(event) => {
                    formContext.setFieldValue('foreignStateDocument', event?.target.value);
                  }}
                  inputProps={{
                    maxLength: '120',
                  }}
                  value={formContext.values?.foreignStateDocument || ''}
                  helperText={formContext.errors?.foreignStateDocument}
                  error={
                    formContext.touched?.foreignStateDocument &&
                    Boolean(formContext.errors?.foreignStateDocument)
                  }
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={4}>
                <MunicipalityList
                  label="Comune"
                  value={formContext.values.municipalityDocument || null}
                  onChange={onChangeMunicipalityDocument}
                  id="municipalityDocument"
                  fromDate={formContext.values.documentIssuedDate}
                  required
                  error={
                    formContext.touched?.municipalityDocument &&
                    Boolean(formContext.errors?.municipalityDocument)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  label="Provincia"
                  aria-label="Provincia"
                  id="documentIssuedProvinceIT"
                  name="documentIssuedProvinceIT"
                  fullWidth
                  onChange={noop}
                  value={formContext.values.municipalityDocument?.province || ''}
                  disabled
                />
              </Grid>
            </>
          )}
        </Grid>
        <Grid item container spacing={2} xs={12} justifyContent="flex-end" display="flex">
          <Grid item xs={12} md={2}>
            <PmiTooltipError show={showTooltip} message={tooltipMessage}>
              <PmiButton variant="contained" color="secondary" fullWidth onClick={handleSearch}>
                Cerca
              </PmiButton>
            </PmiTooltipError>
          </Grid>
        </Grid>
        {dataPersons.length > 1 && visibilityTable ? (
          <Grid item xs={12}>
            <Table
              ariaLabel="Lista Persone"
              page={paginationState.page}
              rowsPerPage={paginationState.rowsPerPage}
              body={dataPersons}
              count={dataPersons.length}
              head={headCells}
              onSort={noop}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              onVisibilityPress={noop}
              orders={{}}
              isNotLayoutFixed
              noSort
              hideButtons
            />
          </Grid>
        ) : undefined}
      </Grid>
    </Fragment>
  );
};

CertificationComponent.displayName = 'Form generality component';

const CertificationForm = memo(CertificationComponent);

export default CertificationForm;
